import React, { useEffect, useRef, useCallback } from 'react';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-luxon';
import { DateTime } from 'luxon';
import moment from 'moment-timezone';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { useSelector } from 'react-redux';
import { getStartTime } from '@/utilities/helpers/commonHelper';
import { formatValueV2 } from '@/Components/Wellness/WellnessUtils';

const GlucoseMeterChart = ({
  chartData,
  userTimezone = 'America/New_York',
  is24hoursformat = true,
  insightStartDateTime,
  insightEndDateTime,
  insightLoading,
  getDateFormatByLocation,
  selectedRange = 'today',
}) => {
  const canvasRef = useRef(null);
  const trans = useSelector(translator);

  const BAR_THICKNESS = 10;

  const getAggregatedData = useCallback(
    (chartDataFn) => {
      const dataArray = Array.isArray(chartDataFn)
        ? chartData.filter((item) => item !== null)
        : chartData
          ? [chartData]
          : [];

      const glucoseConcentrationData = dataArray
        .map((item) => {
          const utcTimestamp = item?.utc_timestamp_inms;
          // Parse as ISO in UTC then convert to userTimezone.
          const timestamp = utcTimestamp
            ? DateTime.fromISO(utcTimestamp, { zone: 'utc' }).setZone(userTimezone).toJSDate()
            : null;
          const val = item?.data?.glucose_concentration;
          let glucose_concentration = null;
          if (typeof val === 'number') {
            glucose_concentration = formatValueV2(Number(val));
          } else if (typeof val === 'string' && !isNaN(parseFloat(val))) {
            glucose_concentration = formatValueV2(Number(parseFloat(val)));
          }
          return { timestamp, glucose_concentration };
        })
        .filter((item) => item.timestamp !== null && item.glucose_concentration !== null)
        .sort((a, b) => a.timestamp - b.timestamp);

      let aggregatedData = [];

      aggregatedData = glucoseConcentrationData.map((h) => {
        return {
          groupTimestamp: h.timestamp,
          actualTimestamp: h.timestamp,
          glucose_concentration: h.glucose_concentration,
        };
      });

      const latestPerMinute = Object.values(
        aggregatedData.reduce((acc, item) => {
          const ts = new Date(item.actualTimestamp);
          const key = DateTime.fromJSDate(ts)
            .toUTC() // normalize timezone
            .toFormat('yyyy-MM-dd HH:mm');

          // keep latest for that minute
          if (!acc[key] || ts > new Date(acc[key].actualTimestamp)) {
            acc[key] = item;
          }

          return acc;
        }, {})
      );

      return latestPerMinute;
    },
    [chartData, userTimezone]
  );

  useEffect(() => {
    if (insightLoading) return;

    // Display timezone information using a custom plugin like in BloodPressureChart
    const tzDisplayText = `${trans('TIMEZONE')}: ${userTimezone}`;

    const timeZonePlugin = {
      id: 'timeZonePlugin',
      afterDatasetsDraw: (chart, args, options) => {
        const { ctx, chartArea } = chart;
        const xAxis = chart.scales.x;
        const xAxisBottom = xAxis ? xAxis.bottom : chartArea.bottom;
        ctx.save();
        ctx.font = options.font || '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = options.color || 'black';
        const text = options.text || '';
        ctx.fillText(text, (chartArea.left + chartArea.right) / 2, xAxisBottom + 20);
        ctx.restore();
      },
    };

    Chart.register(timeZonePlugin);

    const aggregatedData = getAggregatedData(chartData);

    const labels = aggregatedData.map((r) => r.groupTimestamp);

    const chartJsData = {
      labels,
      rawTimestamps: aggregatedData.map((row) => row.actualTimestamp),
      datasets: [
        {
          label: 'mg/dl',
          data: aggregatedData.map((row) => ({
            x: row.groupTimestamp,
            y: row.glucose_concentration,
          })),
          skipNull: true,
          barThickness: BAR_THICKNESS,
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
        },
      ],
    };

    const formatDate = getDateFormatByLocation(userTimezone);
    const tooltipFormatString = is24hoursformat
      ? `${formatDate}, HH:mm:ss`
      : `${formatDate}, hh:mm:ss a`;

    const isToday = String(selectedRange || '').toLowerCase() === 'today';

    const chartObj = {
      type: 'bar',
      data: chartJsData,
      options: {
        animation: { duration: 0 },
        hover: { animationDuration: 0 },
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { bottom: 42 } },
        interaction: { mode: 'nearest', axis: 'x' },
        plugins: {
          title: { display: false },
          tooltip: {
            callbacks: {
              title: (tooltipItems) => {
                const idx = tooltipItems[0].dataIndex;
                const rawTimestamp = tooltipItems[0].chart.data.rawTimestamps[idx];
                return rawTimestamp
                  ? DateTime.fromJSDate(rawTimestamp)
                      .setZone(userTimezone)
                      .toFormat(tooltipFormatString)
                  : '';
              },
            },
          },
          datalabels: { display: false },
          timeZonePlugin: {
            text: tzDisplayText,
            font: '14px Arial',
            color: '#666',
          },
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: isToday ? 'hour' : 'day',
              displayFormats: isToday
                ? { hour: is24hoursformat ? 'HH' : 'ha' }
                : { day: getDateFormatByLocation(userTimezone) },
            },
            min: getStartTime({ selectedRange, userTimezone }),
            max: moment().tz(userTimezone).toDate(),
            title: { display: false },
            grid: { display: true, drawOnChartArea: false },
            border: {
              color: '#656565',
              display: true,
              dash: [5, 5],
              dashOffset: 1,
            },
            ticks: {
              callback: (value) => {
                const date = new Date(value);

                if (isToday) {
                  if (is24hoursformat) {
                    const time = moment.utc(date).tz(userTimezone).format('HH:mm');
                    if (time === '00:00') {
                      return '24:00';
                    }
                    return time;
                  }
                  return moment.utc(date).tz(userTimezone).format('ha');
                }

                return new Intl.DateTimeFormat('en', {
                  month: 'short',
                  day: 'numeric',
                }).format(date); // e.g. "Sep 1"
              },
            },
          },
          y: {
            type: 'linear',
            ticks: { autoSkip: true, precision: true },
            border: {
              color: '#656565',
              display: true,
              dash: [5, 5],
              dashOffset: 1,
            },
            grid: { color: '#989898', lineWidth: 1 },
          },
        },
      },
    };

    const chartInstance = new Chart(canvasRef.current, chartObj);
    return () => {
      chartInstance.destroy();
    };
  }, [
    chartData,
    userTimezone,
    is24hoursformat,
    insightStartDateTime,
    insightEndDateTime,
    insightLoading.selectedRange,
    getDateFormatByLocation,
  ]);

  return (
    <div className="chart-container">
      <canvas ref={canvasRef} className="chart-canvas" />
    </div>
  );
};

export default GlucoseMeterChart;
